import { z } from "zod";
import { findDistrictById } from "../geo";
import { bdMobileSchema } from "./auth";
import { idSchema, poishaSchema } from "./inventory";

/**
 * Scopes a secret key can hold (FR-API-02). `orders:write` includes `orders:create`; a key that
 * should only place orders can be given `orders:create` alone.
 */
export const API_SCOPES = [
  "catalog:read",
  "customers:read",
  "customers:write",
  "orders:read",
  "orders:create",
  "orders:write",
  "sales:read",
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

/** Publishable keys are safe to ship in a browser: catalog reads and placing orders only. */
export const PUBLISHABLE_SCOPES: readonly ApiScope[] = ["catalog:read", "orders:create"];

export function hasScope(scopes: readonly string[], required: ApiScope): boolean {
  if (scopes.includes(required)) return true;
  return required === "orders:create" && scopes.includes("orders:write");
}

export const API_MODES = ["live", "test"] as const;
export type ApiMode = (typeof API_MODES)[number];

export const WEBHOOK_EVENTS = [
  "order.created",
  "order.updated",
  "sale.completed",
  "stock.low",
  "item.updated",
  "customer.created",
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENTS)[number];

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

/** A browser origin such as https://shop.example.com. Plain http is accepted only for localhost. */
export const originSchema = z
  .string()
  .trim()
  .max(200)
  .transform((value, ctx) => {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      ctx.addIssue({ code: "custom", message: "Enter an origin like https://shop.example.com" });
      return z.NEVER;
    }
    const secure =
      url.protocol === "https:" || (url.protocol === "http:" && LOCAL_HOSTS.has(url.hostname));
    if (!secure || url.username || url.password) {
      ctx.addIssue({ code: "custom", message: "Use https (http only for localhost)" });
      return z.NEVER;
    }
    return url.origin;
  });

const keyName = z.string().trim().min(2).max(60);
const expiresAt = z.number().int().positive().nullable().optional();

export const apiKeyCreateSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("publishable"),
    mode: z.enum(API_MODES),
    name: keyName,
    allowedOrigins: z.array(originSchema).min(1, "Add at least one website origin").max(10),
    expiresAt,
  }),
  z.object({
    kind: z.literal("secret"),
    mode: z.enum(API_MODES),
    name: keyName,
    scopes: z
      .array(z.enum(API_SCOPES))
      .min(1, "Choose at least one permission")
      .max(API_SCOPES.length),
    expiresAt,
  }),
]);
export type ApiKeyCreate = z.output<typeof apiKeyCreateSchema>;

/** Webhook URLs must be public https endpoints. */
export const webhookUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !LOCAL_HOSTS.has(url.hostname) && !url.username;
    } catch {
      return false;
    }
  }, "Enter a public https URL");

export const webhookEndpointCreateSchema = z.object({
  mode: z.enum(API_MODES),
  url: webhookUrlSchema,
  description: z.string().trim().max(120).optional(),
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, "Choose at least one event"),
});
export type WebhookEndpointCreate = z.output<typeof webhookEndpointCreateSchema>;

export const webhookEndpointUpdateSchema = z
  .object({
    url: webhookUrlSchema,
    description: z.string().trim().max(120).nullable(),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, "Choose at least one event"),
    status: z.enum(["active", "disabled"]),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");

/**
 * Public quantities are decimals in the item's unit (1.5 = one and a half kg); internally they are
 * milli units, so at most three decimal places are accepted.
 */
export const publicQuantitySchema = z
  .number()
  .positive()
  .max(1_000_000_000)
  .transform((value, ctx) => {
    const milli = Math.round(value * 1000);
    if (Math.abs(value * 1000 - milli) > 1e-6 || milli <= 0) {
      ctx.addIssue({ code: "custom", message: "Use at most three decimal places" });
      return z.NEVER;
    }
    return milli;
  });

export const ORDER_STATUSES = [
  "pending",
  "confirmed",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
  /** Shipped but sent back: the goods are back in stock and nothing is owed. */
  "returned",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_CHANNELS = ["api", "storefront", "manual"] as const;
export type OrderChannel = (typeof ORDER_CHANNELS)[number];

export const orderCreateSchema = z.object({
  /** Defaults to the main branch. */
  branchId: idSchema.optional(),
  customer: z.object({
    name: z.string().trim().min(1).max(120),
    phone: bdMobileSchema,
  }),
  shipping: z.object({
    address: z.string().trim().min(3).max(240),
    districtId: z.string().refine((id) => findDistrictById(id) !== null, "Unknown district"),
    area: z.string().trim().max(80).optional(),
  }),
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        /** Defaults to the item's base unit. */
        unitCode: z.string().min(1).max(16).optional(),
        quantity: publicQuantitySchema,
      }),
    )
    .min(1)
    .max(100),
  deliveryCharge: poishaSchema.default(0),
  payment: z
    .object({
      method: z.enum(["cod", "prepaid"]),
      /** Gateway or MFS transaction reference for prepaid orders. */
      reference: z.string().trim().min(3).max(80).optional(),
    })
    .default({ method: "cod" }),
  note: z.string().trim().max(500).optional(),
  /** Your own order ID, for matching. */
  externalRef: z.string().trim().max(80).optional(),
});
export type OrderCreateInput = z.input<typeof orderCreateSchema>;
export type OrderCreate = z.output<typeof orderCreateSchema>;

const collectSchema = z.object({
  accountId: idSchema,
  txnRef: z.string().trim().min(4).max(40).optional(),
});

/** Moves an order through its fulfilment steps from the order inbox (FR-WEB-05). */
export const orderTransitionSchema = z.discriminatedUnion("to", [
  z.object({ to: z.literal("confirmed") }),
  z.object({ to: z.literal("packed") }),
  z.object({
    to: z.literal("shipped"),
    /** Where the money for a prepaid order was received. Required for prepaid orders. */
    payment: collectSchema.optional(),
  }),
  z.object({
    to: z.literal("delivered"),
    /** Records cash-on-delivery money received into an account. */
    collect: collectSchema
      .extend({
        /** Less than the amount due for a partial delivery; defaults to everything owed. */
        amount: poishaSchema.optional(),
      })
      .optional(),
  }),
  z.object({ to: z.literal("cancelled"), reason: z.string().trim().min(3).max(200) }),
  /** The parcel came back undelivered: restock everything and clear what the shopper owed. */
  z.object({ to: z.literal("returned"), reason: z.string().trim().min(3).max(200) }),
]);
export type OrderTransition = z.output<typeof orderTransitionSchema>;

export const orderCancelSchema = z.object({ reason: z.string().trim().min(3).max(200) });

export const publicCustomerCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: bdMobileSchema,
  address: z.string().trim().max(240).optional(),
});
